import { useApolloClient } from '@apollo/client';
import { ErrorPage, LoadingPage, Modal, ModalForm, TControlledFormComponentProps, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { TListEmployee, useQueryAllEmployees } from '../../../entities/employee/shared';
import { BRICK_SIZE_CONFIG, BRICK_SIZE_OPTIONS, getBrickColorOptions, normalizeBrickText, ONE_OFF_EVENT_TYPE_OPTIONS, TBrickSize, TOneOffEventFormValues } from '../../../entities/events/oneOff';
import { getOneOffEventSchema } from '../../../entities/events/oneOffSchema';
import { ListClientOneOffEventsDocument, TEventTypes, useCreateOneOffEventMutation } from '../../../generated/graphql';
import { usePrivateCompanyContext } from '../../../routes/outlets/PrivateCompanyOutlet';
import { BrickPreview } from './BrickPreview';
import { BrickPricing } from './BrickPricing';
import styles from './OneOffEventForm.module.scss';

type TOneOffEventCreateFormProps = {
  title: string;
  eventTypes: TEventTypes[];
};

const eventTypeHasCustomDescription = (type?: null | TEventTypes) => {
  return type === TEventTypes.Other || type === TEventTypes.CustomBricks;
};

const getErrorMessage = (error: unknown) => {
  return error instanceof Error ? error.message : 'Unknown error';
};

export const OneOffEventCreateForm = ({ title, eventTypes }: TOneOffEventCreateFormProps) => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const company = usePrivateCompanyContext();
  const [createOneOffEvent] = useCreateOneOffEventMutation();
  const { data, error, loading } = useQueryAllEmployees({ companyId: company.id });

  const closeModal = useCallback(() => {
    navigate('..', { relative: 'path' });
  }, [navigate]);

  const employees = useMemo(() => {
    return (data?.listCompanyEmployees.items || []).filter((employee): employee is TListEmployee => Boolean(employee?.active)).sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }, [data?.listCompanyEmployees.items]);

  const employeeOptions = useMemo(() => {
    return employees.map(({ fullName, id }) => ({
      label: fullName || id,
      value: id
    }));
  }, [employees]);

  const formFields = useMemo<TControlledFormComponentProps<TOneOffEventFormValues>[]>(() => {
    const fields: TControlledFormComponentProps<TOneOffEventFormValues>[] = [
      {
        label: 'Employee',
        name: 'employeeIds',
        type: 'select',
        className: styles.fullWidth,
        inputOptions: {
          multiple: true,
          options: employeeOptions,
          placeholder: 'Select one or more employees',
          variant: 'outlined'
        }
      },
      {
        label: 'Event Type',
        name: 'type',
        type: 'select',
        hide: eventTypes.length === 1,
        inputOptions: {
          includeEmptyOption: false,
          multiple: false,
          options: ONE_OFF_EVENT_TYPE_OPTIONS.filter(({ value }) => eventTypes.includes(value)),
          variant: 'outlined'
        }
      },
      /*{
        label: 'Custom event type / description',
        name: 'customType',
        type: 'textField',
        show: ({ type }) => eventTypeHasCustomDescription(type as TEventTypes),
        inputOptions: {
          multiline: false,
          variant: 'outlined'
        }
      },*/
      {
        label: 'Event Date',
        name: 'eventDate',
        type: 'datePicker',
        inputOptions: {
          clearDisabled: true,
          variant: 'outlined'
        }
      },
      {
        label: 'Notes',
        name: 'notes',
        type: 'textArea',
        inputOptions: {
          multiline: true,
          rows: 3,
          variant: 'outlined'
        }
      },
      {
        label: 'Brick Quantity',
        name: 'brickQuantity',
        type: 'textField',
        show: ({ employeeIds }) => employeeIds?.length === 1,
        inputOptions: {
          type: 'number',
          min: 1,
          step: 1,
          supportingText: '* If this is a bulk order and all the bricks are the same, how many bricks do you need delivered?',
          variant: 'outlined'
        }
      },
      {
        title: 'Printed brick',
        type: 'section'
      },
      {
        label: 'Brick Size',
        name: 'brickSize',
        type: 'select',
        inputOptions: {
          includeEmptyOption: false,
          multiple: false,
          options: BRICK_SIZE_OPTIONS,
          variant: 'outlined'
        }
      },
      {
        label: 'Brick Color',
        name: 'brickColor',
        type: 'select',
        inputOptions: ({ brickSize, brickColor }: Partial<TOneOffEventFormValues>) => {
          const selectedSize = brickSize && BRICK_SIZE_CONFIG[brickSize as TBrickSize] ? (brickSize as TBrickSize) : '2x4';

          const colors = getBrickColorOptions(selectedSize);
          const selectedColor = colors.find(({ value }) => value === brickColor);

          return {
            includeEmptyOption: false,
            leadingIcon: selectedColor ? <span aria-hidden='true' className={styles.colorOption} style={{ backgroundColor: selectedColor.background }} /> : undefined,
            multiple: false,
            options: colors.map((color) => ({
              label: color.label,
              trailingIcon: <span aria-hidden='true' className={styles.colorOption} style={{ backgroundColor: color.background }} />,
              value: color.value
            })),
            variant: 'outlined'
          };
        }
      },
      {
        label: 'Text on Brick',
        name: 'brickText',
        type: 'textField',
        className: styles.fullWidth,
        inputOptions: {
          multiline: false,
          supportingText: 'Text is fitted to one centered line from 18 pt down to 6 pt.',
          variant: 'outlined'
        }
      }
    ];
    return fields;
  }, [employeeOptions, eventTypes]);

  const schema = useMemo(
    () =>
      getOneOffEventSchema(
        employees.map(({ id }) => id),
        eventTypes
      ),
    [employees, eventTypes]
  );

  const onSubmit = useCallback<TControlledFormProps<TOneOffEventFormValues>['onValidSubmit']>(
    async (formData, { methods }) => {
      const selectedEmployees = formData.employeeIds.map((employeeId) => employees.find(({ id }) => id === employeeId)).filter((employee): employee is TListEmployee => employee != null);
      const eventDate = typeof formData.eventDate === 'string' ? formData.eventDate : format(formData.eventDate as Date, 'yyyy-MM-dd');
      const customType = eventTypeHasCustomDescription(formData.type) ? formData.customType || null : null;
      const brickText = normalizeBrickText(formData.brickText);

      let successCount = 0;
      for (const employee of selectedEmployees) {
        try {
          // Tenant, recipient, and source are derived authoritatively by the API from the client identity.
          const result = await createOneOffEvent({
            variables: {
              input: JSON.stringify({
                age: 0,
                employeeId: employee.id,
                eventDate,
                type: formData.type,
                customType,
                notes: formData.notes || null,
                brickSize: formData.brickSize,
                brickColor: formData.brickColor,
                brickText,
                brickQuantity: selectedEmployees.length === 1 ? formData.brickQuantity : 1
              })
            }
          });
          const response = result.data?.createOneOffEvent;

          if (!response || response.error || response.status >= 400) throw new Error(response?.message || 'The API did not confirm that the event was created.');

          successCount += 1;
        } catch (error) {
          console.error('One-off event creation failed', { employeeId: employee.id, error: serializeError(error) });
          toast.error(`Could not create the event for ${employee.fullName || employee.id}: ${getErrorMessage(error)}`);
          break;
        }
      }
      const remainingEmployeeIds = selectedEmployees.slice(successCount).map((employee) => employee.id);

      if (successCount > 0)
        try {
          await client.refetchQueries({ include: [ListClientOneOffEventsDocument] });
        } catch (refetchError) {
          console.error(serializeError(refetchError));
          toast.warning('The events were created, but the list could not be refreshed automatically.');
        }

      if (remainingEmployeeIds.length === 0) {
        toast.success(successCount === 1 ? 'Successfully created the one-off event.' : `Successfully created ${successCount} one-off events.`);
        closeModal();
        return;
      }

      if (selectedEmployees.length > 1) methods.setValue('brickQuantity', 1);
      methods.setValue('employeeIds', remainingEmployeeIds, { shouldValidate: true });

      if (successCount > 0) toast.warning(`${successCount} event${successCount === 1 ? '' : 's'} created; ${remainingEmployeeIds.length} remaining. The remaining employees stay selected so you can retry.`);
      else toast.error('No one-off events were created. The selected employees remain available so you can retry.');
    },
    [client, closeModal, createOneOffEvent, employees]
  );

  if (error)
    return (
      <Modal closeModal={closeModal} headline='New Event' size='large'>
        <ErrorPage error={error} />
      </Modal>
    );

  if (data == null || loading)
    return (
      <Modal closeModal={closeModal} headline='New Event' size='large'>
        <LoadingPage />
      </Modal>
    );

  return (
    <ModalForm<TOneOffEventFormValues>
      closeModal={closeModal}
      confirmLabel='Create'
      controlledFormProps={{
        children: (
          <>
            <BrickPreview />
            <BrickPricing />
          </>
        ),
        className: styles.form,
        fields: formFields,
        formProps: {
          defaultValues: {
            brickQuantity: 1,
            brickColor: 'black',
            brickSize: '2x4',
            brickText: '',
            customType: null,
            employeeIds: [],
            eventDate: new Date(),
            notes: null,
            type: eventTypes.length === 1 ? eventTypes[0] : null
          },
          resolver: joiResolver(schema, {
            abortEarly: false,
            convert: true,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit
      }}
      headline={`New ${title} Order`}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      size='large'
      supportingText='Create the same one-off event for one or more employees in your company.'
    />
  );
};
